// Secure logging utility to prevent sensitive data exposure
export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

interface LogContext {
  userId?: string;
  action?: string;
  component?: string;
  timestamp?: string;
}

class SecureLogger {
  private isDevelopment = import.meta.env.DEV;
  private logLevel: LogLevel;

  constructor() {
    // Reduce logging for security - only errors and warnings
    this.logLevel = LogLevel.WARN;
  }

  private sanitizeData(data: any): any {
    if (typeof data !== 'object' || data === null) {
      return data;
    }

    const sensitiveKeys = [
      'token', 'password', 'auth', 'authorization', 'cookie', 'session',
      'secret', 'key', 'email', 'phone', 'address', 'ssn', 'credit',
      'apikey', 'api_key', 'access_token', 'refresh_token'
    ];

    const sanitized = { ...data };
    
    for (const key in sanitized) {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof sanitized[key] === 'object') {
        sanitized[key] = this.sanitizeData(sanitized[key]);
      }
    }

    return sanitized;
  }

  private log(level: LogLevel, message: string, data?: any, context?: LogContext) {
    if (level > this.logLevel) return;

    const timestamp = new Date().toISOString();
    const sanitizedData = data ? this.sanitizeData(data) : undefined;
    
    const logEntry = {
      timestamp,
      level: LogLevel[level],
      message,
      ...(context && { context }),
      ...(sanitizedData && { data: sanitizedData })
    };

    switch (level) {
      case LogLevel.ERROR:
        console.error(`[ERROR] ${message}`, sanitizedData || '');
        break;
      case LogLevel.WARN:
        console.warn(`[WARN] ${message}`, sanitizedData || '');
        break;
      case LogLevel.INFO:
        if (this.isDevelopment) {
          console.info(`[INFO] ${message}`, sanitizedData || '');
        }
        break;
      case LogLevel.DEBUG:
        if (this.isDevelopment) {
          console.log(`[DEBUG] ${message}`, sanitizedData || '');
        }
        break;
    }

    // In production, you could send critical logs to a monitoring service
    if (level <= LogLevel.ERROR && !this.isDevelopment) {
      this.sendToMonitoring(logEntry);
    }
  }

  private sendToMonitoring(logEntry: any) {
    // Placeholder for external monitoring service (e.g., Sentry, LogRocket)
    // fetch('/api/logs', { method: 'POST', body: JSON.stringify(logEntry) });
  }

  error(message: string, data?: any, context?: LogContext) {
    this.log(LogLevel.ERROR, message, data, context);
  }

  warn(message: string, data?: any, context?: LogContext) {
    this.log(LogLevel.WARN, message, data, context);
  }

  info(message: string, data?: any, context?: LogContext) {
    this.log(LogLevel.INFO, message, data, context);
  }

  debug(message: string, data?: any, context?: LogContext) {
    this.log(LogLevel.DEBUG, message, data, context);
  }

  // Security event logging for admin actions
  securityEvent(action: string, userId: string, details?: any) {
    const securityLog = {
      type: 'SECURITY_EVENT',
      action,
      userId,
      timestamp: new Date().toISOString(),
      details: this.sanitizeData(details),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Always log security events, even in production
    console.warn(`[SECURITY] ${action} by user ${userId}`, securityLog);
    
    // Send to monitoring service in production
    if (!this.isDevelopment) {
      this.sendToMonitoring(securityLog);
    }
  }

  // Admin action logging
  adminAction(action: string, adminId: string, targetId?: string, details?: any) {
    this.securityEvent(`ADMIN_${action}`, adminId, {
      targetId,
      ...details
    });
  }
}

export const logger = new SecureLogger();

// Utility for error boundaries
export const handleError = (error: Error, context?: LogContext) => {
  logger.error('Unhandled error', {
    message: error.message,
    stack: error.stack,
    name: error.name
  }, context);
};